import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles } from "lucide-react";

const emptyLine = () => ({ name: "", qty: 1, cost: 0, inventory_id: "" });

export default function PurchaseInvoices() {
  const qc = useQueryClient();
  const [vendorId, setVendorId] = useState("");
  const [vendor, setVendor] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [raw, setRaw] = useState("");
  const [items, setItems] = useState([emptyLine()]);
  const { data: list = [] } = useQuery({ queryKey: ["purchase-invoices"], queryFn: async () => (await api.get("/purchase-invoices")).data });
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data });
  const { data: inventory = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const pickVendor = (id) => {
    setVendorId(id);
    const v = vendors.find((x) => x.id === id);
    if (v) setVendor(v.name || "");
  };

  const setLine = (idx, patch) => {
    setItems((rows) => rows.map((x, i) => {
      if (i !== idx) return x;
      const next = { ...x, ...patch };
      if (patch.inventory_id != null) {
        const inv = inventory.find((n) => n.id === patch.inventory_id);
        if (inv) {
          next.name = inv.name || next.name;
          if (inv.cost_per_unit != null && (next.cost === 0 || next.cost === "")) next.cost = Number(inv.cost_per_unit);
        }
      }
      return next;
    }));
  };

  const parse = async () => {
    try {
      const { data } = await api.post("/purchase-invoices/parse", { text: raw });
      if (data.vendor_name) {
        setVendor(data.vendor_name);
        const match = vendors.find((v) => String(v.name || "").toLowerCase() === String(data.vendor_name).toLowerCase());
        setVendorId(match?.id || "");
      }
      if (data.invoice_number) setInvoiceNo(data.invoice_number);
      if (Array.isArray(data.items) && data.items.length) {
        setItems(data.items.map((i) => {
          const inv = inventory.find((n) => String(n.name || "").toLowerCase() === String(i.name || "").toLowerCase());
          return {
            name: i.name || "",
            qty: Number(i.qty || 1),
            cost: Number(i.cost || 0),
            inventory_id: inv?.id || "",
          };
        }));
      }
      toast.success(`Parsed via ${data.provider}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Parse failed"); }
  };

  const save = async () => {
    try {
      await api.post("/purchase-invoices", {
        vendor_id: vendorId || undefined,
        vendor_name: vendor,
        invoice_number: invoiceNo,
        items: items.filter((i) => i.name || i.inventory_id).map((i) => ({
          name: i.name,
          qty: Number(i.qty || 0),
          cost: Number(i.cost || 0),
          inventory_id: i.inventory_id || undefined,
        })),
        receive: true,
      });
      toast.success("Invoice saved · stock updated");
      setItems([emptyLine()]); setRaw(""); setInvoiceNo(""); setVendor(""); setVendorId("");
      qc.invalidateQueries({ queryKey: ["purchase-invoices"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8" data-testid="purchase-invoices">
      <PageHeader title="Purchase invoices" subtitle="Receive stock from vendor bills · AI parse when OpenAI key is set" />
      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="kul-card p-5 space-y-3">
          <div><Label>Paste invoice text</Label><Textarea rows={6} value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="Vendor / item qty @ rate…" /></div>
          <Button variant="outline" onClick={parse}><Sparkles className="w-4 h-4 mr-1" /> Parse invoice</Button>
          <div>
            <Label>Vendor</Label>
            <select
              className="w-full h-9 rounded-lg border px-2 text-sm mb-2"
              value={vendorId}
              onChange={(e) => pickVendor(e.target.value)}
              data-testid="purchase-vendor"
            >
              <option value="">Select vendor…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <Input value={vendor} onChange={(e) => { setVendor(e.target.value); setVendorId(""); }} placeholder="Or type vendor name" />
          </div>
          <div><Label>Invoice #</Label><Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} /></div>
          {items.map((it, idx) => (
            <div key={idx} className="space-y-1 border-b border-neutral-100 pb-2">
              <select
                className="w-full h-9 rounded-lg border px-2 text-sm"
                value={it.inventory_id || ""}
                onChange={(e) => setLine(idx, { inventory_id: e.target.value })}
                data-testid={`purchase-inv-${idx}`}
              >
                <option value="">Link inventory SKU (optional)…</option>
                {inventory.map((n) => <option key={n.id} value={n.id}>{n.name} · stock {n.stock}</option>)}
              </select>
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Item" value={it.name} onChange={(e) => setLine(idx, { name: e.target.value })} />
                <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => setLine(idx, { qty: Number(e.target.value) })} />
                <Input type="number" placeholder="Cost" value={it.cost} onChange={(e) => setLine(idx, { cost: Number(e.target.value) })} />
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={() => setItems([...items, emptyLine()])}>Add line</Button>
          <Button onClick={save} className="bg-[var(--kul-primary)] text-white w-full">Receive & update stock</Button>
        </div>
        <div className="space-y-2">
          {list.map((inv) => (
            <div key={inv.id} className="kul-card p-4 text-sm">
              <div className="font-medium">{inv.vendor_name || "Vendor"} · {inv.invoice_number || "—"}</div>
              <div className="text-neutral-500">{inv.status} · {money(inv.total, sym)}</div>
              <div className="text-xs text-neutral-400 mt-1">{(inv.items || []).length} lines · {inv.created_at}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
